import { getUserCity } from '@firsttable/functions';
import { useGrowthBook } from '@growthbook/growthbook-react';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import useFeatureFlagValue from '@/components/analytics/hooks/useFeatureFlagValue';
import { PD7364_REDIRECT_LOGGED_IN_USERS_FROM_HOMEPAGE_FLAG } from './flag';

const HOMEPAGE_SEEN_KEY = 'pd7364-homepage-seen';

/** PD-7364: on the first homepage view this session, send a logged-in variant user to their city list. */
export default function usePD7364RedirectToCityList(): void {
  const growthbook = useGrowthBook();
  const isVariant =
    useFeatureFlagValue(PD7364_REDIRECT_LOGGED_IN_USERS_FROM_HOMEPAGE_FLAG, false) === true;
  // The exposure is recorded with the current attributes, so wait for device_id and logged_in.
  const attributes = growthbook?.getAttributes();
  const flagSettled = Boolean(
    growthbook?.ready && attributes?.device_id && attributes.logged_in === true
  );
  const router = useRouter();

  useEffect(() => {
    if (!router.isReady) return;

    // useFeatureFlagValue applies ?flag= one render late, so this first check reads the URL.
    let queryOverride: string | null = null;
    try {
      queryOverride = new URLSearchParams(window.location.search).get(
        PD7364_REDIRECT_LOGGED_IN_USERS_FROM_HOMEPAGE_FLAG
      );
    } catch {
      return;
    }

    let alreadySeen = false;
    try {
      alreadySeen = window.sessionStorage.getItem(HOMEPAGE_SEEN_KEY) === '1';
    } catch {
      return;
    }
    if (alreadySeen) return;

    if (queryOverride === null && !flagSettled) return;

    const variant = queryOverride !== null ? queryOverride === 'true' : isVariant;
    const destination = getUserCity();
    // Mark the visit even with no redirect, so opening the homepage again in this tab stays put.
    try {
      window.sessionStorage.setItem(HOMEPAGE_SEEN_KEY, '1');
    } catch {
      return;
    }
    if (!variant || !destination) return;

    router.replace(destination);
  }, [flagSettled, isVariant, router]);
}
